/**
 * Forgot Password Page
 * Uses Supabase Auth for password reset
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/auth/AuthLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { validateEmail } from '../utils/validation';
import { Loader2, AlertCircle, CheckCircle, ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setErrors({});
    setErrorMessage('');
    setSuccess(false);

    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!validateEmail(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    try {
      const result = await resetPassword(email);
      if (result.success) {
        setSuccess(true);
      } else {
        setErrorMessage(result.error || 'Failed to send reset link. Please try again.');
      }
    } catch (error: any) {
      setErrorMessage('Failed to send reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout showSidePanel={false}>
      <div className="text-center mb-8">
        <h1
          className="text-4xl font-bold text-slate-800"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
        >
          UserLife JML
        </h1>
      </div>

      <Card
        className="w-full border-slate-200 shadow-lg rounded-2xl"
        data-testid="forgot-password-card"
      >
        <CardHeader className="space-y-1 pb-6">
          <CardTitle
            className="text-2xl sm:text-3xl font-semibold text-slate-800"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Reset your password
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            Enter your email address and we&apos;ll send you a link to reset your password
          </CardDescription>
        </CardHeader>

        <CardContent>
          {success && (
            <Alert
              className="mb-6 border-emerald-200 bg-emerald-50 text-emerald-800"
              data-testid="success-alert"
            >
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>Email Sent Successfully</AlertTitle>
              <AlertDescription>
                If an account exists with {email}, you will receive a password reset link shortly.
              </AlertDescription>
            </Alert>
          )}

          {errorMessage && (
            <Alert variant="destructive" className="mb-6" data-testid="error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e: any) => setEmail(e.target.value)}
                    className="h-11 pl-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                    data-testid="email-input"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  <Mail
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={20}
                  />
                </div>
                {errors.email && (
                  <p
                    id="email-error"
                    className="text-sm text-red-600 flex items-center gap-1 mt-1"
                    role="alert"
                    data-testid="email-error"
                  >
                    <AlertCircle size={16} aria-hidden="true" />
                    {errors.email}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                data-testid="submit-button"
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    <span>Sending link...</span>
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}

          <div className="mt-6">
            <Link
              to="/app/login"
              className="flex items-center justify-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors duration-200"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={16} />
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default ForgotPassword;
